import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactFlowProvider } from "reactflow";
import type { ReactFlowInstance } from "reactflow";
import "reactflow/dist/style.css";
import { useWorkflow } from "./hooks/useWorkflow";
import { usePreviousNodes } from "./hooks/usePreviousNodes";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import Sidebar from "./components/sidebar/Sidebar";
import Toolbar from "./components/toolbar/Toolbar";
import WorkflowCanvas from "./components/canvas/WorkflowCanvas";
import NodeConfigurationModal from "./components/modals/NodeConfigurationModal";
import CustomNode from "./components/nodes/CustomNode";
import EdgeWithButton from "./components/canvas/EdgeWithButton";
import TriggerNode from "./components/nodes/TriggerNode";
import ConditionNode from "./components/nodes/ConditionNode";
import ActionNode from "./components/nodes/ActionNode";
import ConfirmationModal from "../../ers/common/components/ConfirmationModal";
import { fetchNodeDynamicData } from "../../../core/data/redux/automations/thunks";
import {
  selectActiveCompany,
  selectNodeDynamicDataMap,
} from "../../../core/data/redux/automations/selector";
import type { NodeData } from "./types/workflow.types";
import type { WorkflowCategory } from "./utils/enums";
import "./styles/WorkflowEditor.css";
import { notify } from "../../../services/toast.config";

interface WorkflowEditorProps {
  nodeConfigs: any;
  workflowType?: WorkflowCategory;
  initialWorkflow?: any;
}

const WorkflowEditor: React.FC<WorkflowEditorProps> = ({
  nodeConfigs,
  workflowType,
  initialWorkflow,
}) => {
  const dispatch = useDispatch<any>();
  const activeCompany = useSelector(selectActiveCompany);
  const nodeDynamicDataMap = useSelector(selectNodeDynamicDataMap);

  const [configModalVisible, setConfigModalVisible] = React.useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [reactFlowInstance, setReactFlowInstance] =
    React.useState<ReactFlowInstance | null>(null);

  const workflowLoadedRef = React.useRef(false);

  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    onEdgeClick,
    addNode,
    updateNodeData,
    deleteNode,
    deleteEdge,
    clearWorkflow,
    exportWorkflow,
    exportWorkflowForBackend,
    importWorkflow,
    selectedNodeId,
    setSelectedNodeId,
    selectedEdgeId,
    setSelectedEdgeId,
    getSelectedNode,
    workflowName,
    setWorkflowName,
    selectedNodes,
    setWorkflowId,
  } = useWorkflow(workflowType, nodeConfigs, nodeDynamicDataMap);

  React.useEffect(() => {
    if (initialWorkflow && !workflowLoadedRef.current) {
      importWorkflow(initialWorkflow, true);
      workflowLoadedRef.current = true;
    }
  }, [initialWorkflow]);

  const { previousContextFields } = usePreviousNodes(
    selectedNodeId,
    nodes,
    edges
  );

  useKeyboardShortcuts({
    selectedNodeId,
    selectedEdgeId,
    deleteNode,
    deleteEdge,
    selectedNodes,
  });

  const nodeTypes = useMemo(
    () => ({
      custom: CustomNode,
      trigger: TriggerNode,
      condition: ConditionNode,
      action: ActionNode,
    }),
    []
  );

  const edgeTypes = useMemo(() => ({ custom: EdgeWithButton }), []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      const nodeData = JSON.parse(e.dataTransfer.getData("nodeData"));
      const position = reactFlowInstance?.screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });
      if (!position) return;

      try {
        let dynamicData = nodeDynamicDataMap[nodeData.nodeKey];
        if (!dynamicData) {
          const result = await dispatch(
            fetchNodeDynamicData({
              companyId: activeCompany?.company_id,
              companyType: activeCompany?.company_type,
              nodeKey: nodeData?.nodeKey,
            })
          ).unwrap();
          dynamicData = result.data;
        }

        const enrichedNode: NodeData = {
          type: nodeData.type,
          label: nodeData.label,
          category: nodeData.category,
          icon: nodeData.icon,
          color: nodeData.color,
          nodeKey: nodeData.nodeKey,
          fields: dynamicData?.nodeSchema?.fields || [],
          contextFields: dynamicData?.contextFields || [],
          dynamicData: dynamicData?.dynamicData || {},
          config: {},
        };

        addNode(enrichedNode, position);
      } catch {
        notify.error("Error dropping node!");
      }
    },
    [dispatch, reactFlowInstance, activeCompany, addNode, nodeDynamicDataMap]
  );

  const onNodeClick = useCallback(
    (_: any, node: any) => {
      setSelectedNodeId(node.id);
      setSelectedEdgeId(null);
      setConfigModalVisible(true);
    },
    [setSelectedNodeId, setSelectedEdgeId]
  );

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  }, [setSelectedNodeId, setSelectedEdgeId]);

  const handleSaveConfiguration = useCallback(
    (config: Record<string, any>) => {
      if (selectedNodeId) updateNodeData(selectedNodeId, config);
      setConfigModalVisible(false);
    },
    [selectedNodeId, updateNodeData]
  );

  const handleSave = useCallback(() => {
    const workflow = exportWorkflow();
    const blob = new Blob([JSON.stringify(workflow, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${workflowName.replace(/\s+/g, "_")}.json`;
    link.click();
    URL.revokeObjectURL(url);
    notify.success("Workflow downloaded successfully");
  }, [exportWorkflow, workflowName]);

  const handleLoad = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const workflow = JSON.parse(event.target?.result as string);
          importWorkflow(workflow, false);
          notify.success("Workflow loaded successfully");
        } catch {
          notify.error("Invalid workflow file");
        }
      };
      reader.readAsText(file);
      e.target.value = "";
    },
    [importWorkflow]
  );

  const handleDeleteNode = useCallback(() => {
    if (selectedNodeId) {
      deleteNode(selectedNodeId);
      notify.success("Node deleted successfully");
    }
  }, [selectedNodeId, deleteNode]);

  const handleClearWorkflow = useCallback(() => {
    clearWorkflow();
    setConfirmModalOpen(false);
    notify.success("Workflow cleared");
  }, [clearWorkflow]);

  const selectedNode = getSelectedNode();

  return (
    <div className="workflow-editor">
      <Sidebar workflow={exportWorkflow()} nodeConfigs={nodeConfigs} />
      <div className="editor-main">
        <Toolbar
          onSave={handleSave}
          onLoad={handleLoad}
          onClear={() => setConfirmModalOpen(true)}
          onDeleteNode={handleDeleteNode}
          hasSelectedNode={!!selectedNode}
          workflowName={workflowName}
          onWorkflowNameChange={setWorkflowName}
          exportWorkflow={exportWorkflow}
          exportWorkflowForBackend={exportWorkflowForBackend}
          setWorkflowId={setWorkflowId}
        />
        <WorkflowCanvas
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          onPaneClick={onPaneClick}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onInit={setReactFlowInstance}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
        />
      </div>
      <NodeConfigurationModal
        visible={configModalVisible}
        onClose={() => setConfigModalVisible(false)}
        nodeData={selectedNode?.data}
        config={selectedNode?.data?.config || {}}
        onSave={handleSaveConfiguration}
        previousContextFields={previousContextFields}
      />
      <ConfirmationModal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        title="Clear Workflow!"
        onConfirm={handleClearWorkflow}
        type="danger"
        message="Are you sure you want to clear the entire workflow?"
      />
    </div>
  );
};

const WorkflowEditorWithProvider: React.FC<WorkflowEditorProps> = (props) => (
  <ReactFlowProvider>
    <WorkflowEditor {...props} />
  </ReactFlowProvider>
);

export default WorkflowEditorWithProvider;
